import Swal from 'sweetalert2';
import { getAxiosClient } from '~src/api';
import { onErrorShowMessage } from '~src/errors';
import { getState } from '~src/state';
import { trackEvent } from '~src/tracking';
import { changeImageSrc, getBase64ImageByUrl } from '~src/utils';
import { triggerStateChangedRequest, b64toBlob } from '~core';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const startLoader = () => {
  const loadingMessages = [
    { icon: '🎨', text: "Analizzando l'immagine originale..." },
    { icon: '👔', text: 'Studiando il nuovo abito richiesto...' },
    { icon: '✂️', text: 'Rimuovendo i vestiti attuali...' },
    { icon: '🧵', text: 'Applicando il nuovo tessuto...' },
    { icon: '🎯', text: 'Adattando alla corporatura...' },
    { icon: '✨', text: 'Rifinitura dei dettagli...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ];

  let currentMessageIndex = 0;
  let messageInterval;

  const swalInstance = Swal.fire({
    title: '✨ Creazione in corso',
    html: `
      <div class="ai-loader-container">
        <div class="loader-visual">
          <div class="loader-circle">
            <div class="loader-progress"></div>
            <div class="loader-icon">${loadingMessages[0].icon}</div>
          </div>
        </div>
        
        <div class="status-message">
          <span class="status-icon">${loadingMessages[0].icon}</span>
          <span class="status-text">${loadingMessages[0].text}</span>
        </div>
        
        <div class="progress-bar-container">
          <div class="progress-bar-fill"></div>
        </div>
        
        <div class="warning-text">
          Non chiudere questa pagina durante l'elaborazione 🙏
        </div>
      </div>
    `,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showConfirmButton: false,
    showCancelButton: false,
    customClass: {
      popup: 'animated fadeIn',
    },
    didOpen: () => {
      const container = Swal.getHtmlContainer();
      if (!container) return;

      const statusIcon = container.querySelector('.status-icon');
      const statusText = container.querySelector('.status-text');
      const loaderIcon = container.querySelector('.loader-icon');

      if (!statusIcon || !statusText || !loaderIcon) {
        console.error('Could not find loader elements');
        return;
      }

      // Update message every 2.4 seconds (doubled from 1.2)
      const updateMessage = (message) => {
        // Reset animations
        statusIcon.style.animation = '';
        statusText.style.animation = '';

        // Force reflow to restart animation
        void statusIcon.offsetWidth;
        void statusText.offsetWidth;

        // Update content and restart animations
        statusIcon.textContent = message.icon;
        statusText.textContent = message.text;
        statusIcon.style.animation = 'iconPop 0.3s ease-out';
        statusText.style.animation = 'fadeIn 0.3s ease-out';

        // Update central icon in sync with status icon
        loaderIcon.textContent = message.icon;
        loaderIcon.style.animation = '';
        void loaderIcon.offsetWidth; // Force reflow
        loaderIcon.style.animation = 'loaderPulse 2s ease-in-out infinite';
      };

      messageInterval = setInterval(() => {
        currentMessageIndex++;
        if (currentMessageIndex < loadingMessages.length) {
          updateMessage(loadingMessages[currentMessageIndex]);
        }
      }, 2400);

      // Store the update function and interval reference on the swal instance
      swalInstance.updateMessage = updateMessage;
      swalInstance.messageInterval = messageInterval;
    },
    willClose: () => {
      if (messageInterval) {
        clearInterval(messageInterval);
      }
    },
  });

  return swalInstance;
};

const stopLoader = () => {
  Swal.hideLoading();
  Swal.close();
};

export default async function requestChangeClothes(clothingUrl, prompt, color) {
  const startTime = performance.now();

  const loaderInstance = startLoader();

  try {
    // Get the original image URL from the selection
    const currentImageUrl = getState('changeClothesOriginalImage');

    if (!currentImageUrl) {
      throw new Error('No current image URL found');
    }

    const fullPrompt = `Change the clothes of the subject to a ${prompt}. Preserve the identify of the subject.`;

    // Prepare data for AI Edit API
    const data = {
      image_url: currentImageUrl,
      prompt: fullPrompt,
      output_format: 'png',
    };

    const maxAttempts = 100;
    const httpClient = await getAxiosClient();
    const httpClientWithRetries = await getAxiosClient(true, maxAttempts);

    // Queue the AI Edit request
    let queueResponse;
    try {
      queueResponse = await httpClient.post(
        `${process.env.GIOTTO_API_URL}/ai-edit`,
        data
      );
    } catch (err) {
      console.error('Error queuing AI Edit:', err);
      stopLoader();
      return onErrorShowMessage();
    }

    // Wait a bit before checking status
    await sleep(1000);

    // Poll for result
    let response;
    try {
      response = await httpClientWithRetries.get(
        `${process.env.GIOTTO_API_URL}/ai-edit?requestId=${queueResponse.data.requestId}`
      );
    } catch (err) {
      console.error('Error getting AI Edit result:', err);
      stopLoader();
      return onErrorShowMessage();
    }

    // The AI Edit API returns the image URL directly
    const aiEditImageUrl = response.data.imageUrl;

    // Stop automatic message rotation and show saving step
    if (loaderInstance.messageInterval) {
      clearInterval(loaderInstance.messageInterval);
    }
    if (loaderInstance.updateMessage) {
      loaderInstance.updateMessage({
        icon: '💾',
        text: 'Salvando la foto tra gli allegati...',
      });

      // Also update the progress bar to show near completion
      const progressBar = document.querySelector('.progress-bar-fill');
      if (progressBar) {
        progressBar.style.animation = 'none';
        progressBar.style.width = '90%';
        progressBar.style.transition = 'width 0.5s ease-out';
      }
    }

    // Save to attachments
    const imageBase64 = await getBase64ImageByUrl(aiEditImageUrl);
    const blob = b64toBlob(imageBase64, 'image/jpeg');
    const attachmentsUploader = getState('uploader.allegati');

    if (!attachmentsUploader) {
      throw new Error('Attachments uploader not initialized');
    }

    const originalImageUrl = getState('changeClothesOriginalImage');

    attachmentsUploader.onComplete = attachmentsUploader.addFile({
      type: blob.type,
      data: blob,
      meta: {
        customAttachmentProps: {
          aiEditRawImage: originalImageUrl,
          isAiEdit: true,
          aiEditPrompt: fullPrompt,
          aiEditClothingUrl: clothingUrl || null,
          aiEditColor: color || null,
        },
      },
    });

    const responseUpload = await attachmentsUploader.upload();

    if (!responseUpload.successful || responseUpload.successful.length === 0) {
      throw new Error('Upload to attachments failed');
    }

    attachmentsUploader.cancelAll();

    const newImageUrl = `${process.env.EDGE_RESIZER}${
      new URL(responseUpload.successful[0].uploadURL).pathname
    }`;

    // Apply changes directly to canvas like applyChangeClothes does
    const selection = getState('changeClothesSelection');
    let magicFrame = selection._magicFrame;
    if (selection.type === 'MagicFrame') {
      magicFrame = selection;
    }

    if (magicFrame) {
      await magicFrame.updatePerson(newImageUrl);
      magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
      magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
    } else {
      const currentProps = {
        scaleX: selection.scaleX,
        scaleY: selection.scaleY,
        top: selection.top,
        left: selection.left,
      };

      await changeImageSrc(selection, newImageUrl);

      selection.set({
        ...currentProps,
      });
    }

    window.canvas.renderAll();
    triggerStateChangedRequest();

    stopLoader();

    trackEvent('AI ChangeClothes', {
      success: true,
      time_taken: parseInt(performance.now() - startTime, 10),
    });

    // Show success message with enhanced UI
    Swal.fire({
      title: '✨ Trasformazione Completata!',
      html: `
        <div class="success-container">
          <div class="success-icon-wrapper">
            <div class="sparkle-container">
              <div class="success-sparkle"></div>
              <div class="success-sparkle"></div>
              <div class="success-sparkle"></div>
              <div class="success-sparkle"></div>
              <div class="success-sparkle"></div>
              <div class="success-sparkle"></div>
            </div>
            <div class="success-circle">
              <div class="success-icon">✅</div>
            </div>
          </div>
          <div class="success-message">L'abito è stato applicato con successo!</div>
        </div>
      `,
      timer: 3000,
      showConfirmButton: false,
      customClass: {
        popup: 'animated fadeIn',
      },
    });
  } catch (error) {
    console.error('Error in requestChangeClothes:', error);
    stopLoader();

    // Clean up uploader if it exists
    const attachmentsUploader = getState('uploader.allegati');
    if (attachmentsUploader) {
      attachmentsUploader.cancelAll();
    }

    trackEvent('AI ChangeClothes', {
      success: true,
      time_taken: parseInt(performance.now() - startTime, 10),
    });
    onErrorShowMessage();
  }
}
